import { useEffect, useState, useRef } from 'react'
import Modal from 'antd/es/modal'
import Form from 'antd/es/form'
import Input from 'antd/es/input'
import Checkbox from 'antd/es/checkbox'
import Select from 'antd/es/select'
import DatePicker from 'antd/es/date-picker'
import { CreateEmployeeReport, EmployeeReport } from 'models/employee-reports'
import moment from 'moment'
import { positiveFloatRegex, normalizePositiveFloat, reportStatuses } from 'models'
import Button from 'components/button'
import { Employee } from 'models/employees'
import { TimePicker } from 'antd'
import { Customer } from 'models'
import { millisecondsToDuration } from 'functions/helpers'
import { Calendar } from 'primereact/calendar'
import { isDate } from 'lodash'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { MobileTimePicker } from '@mui/x-date-pickers/MobileTimePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import TextField from '@mui/material/TextField'
import fiLocale from 'date-fns/locale/fi'

type Props = {
  currentUserId: string | undefined
  isAdmin: boolean
  visible: boolean
  onClose: () => void
  onCreate: (employeeReport: CreateEmployeeReport) => void
  onUpdate: (employeeReport: EmployeeReport) => void
  onDelete: (employeeReport: EmployeeReport) => void
  report: CreateEmployeeReport | undefined
  customers: Customer[] | undefined
  employees: Employee[] | undefined
}

export const CreateReportModal = (props: Props) => {
  const [form] = Form.useForm<CreateEmployeeReport>()

  const [selectedDate, _setSelectedDate] = useState<Date | undefined>(undefined)
  const refSelectedDate = useRef<Date | undefined>(undefined)

  const setSelectedDate = (date: Date | undefined) => {
    refSelectedDate.current = date
    _setSelectedDate(date)
  }

  const [startTimeIsoDate, setStartTimeIsoDate] = useState<Date | undefined>(undefined)
  const [endTimeIsoDate, setEndTimeIsoDate] = useState<Date | undefined>(undefined)
  const [customerId, setCustomerId] = useState<string | undefined>(undefined)
  const [employeeId, setEmployeeId] = useState<string | undefined>(undefined)
  const [totalWorkTime, setTotalWorkTIme] = useState<number | undefined>(undefined)
  const [lunchBreakDuration, setLunchBreakDuration] = useState<number>(0)
  const [status, setStatus] = useState<CreateEmployeeReport['status'] | undefined>(undefined)

  useEffect(() => {
    form.resetFields()
    if (props.report) {
      form.setFieldsValue({
        ...props.report,
        start_time_iso_date: moment(props.report.start_time_iso_date),
        end_time_iso_date: moment(props.report.end_time_iso_date)
      })
      if (props.report?.start_time_iso_date) {
        setStartTimeIsoDate(new Date(props.report.start_time_iso_date))
      }
      if (props.report?.end_time_iso_date) {
        setEndTimeIsoDate(new Date(props.report.end_time_iso_date))
      }
      if (props.report?.employee_id) {
        setEmployeeId(props.report.employee_id)
      }
      if (props.report?.customer_id) {
        setCustomerId(props.report.customer_id)
      }
      if (props.report.data.total_work_time) {
        setTotalWorkTIme(props.report.data.total_work_time)
      }
      if (props.report.start_time_iso_date) {
        setSelectedDate(props.report.start_time_iso_date)
      }
      if (props.report.data.lunch_break_duration) {
        setLunchBreakDuration(props.report.data.lunch_break_duration)
      }
      setStatus(props.report.status)
    } else {
      setCustomerId(undefined)
      setStartTimeIsoDate(undefined)
      setEndTimeIsoDate(undefined)
      setTotalWorkTIme(undefined)
      setStatus('open')
      setSelectedDate(undefined)
      setLunchBreakDuration(0)
      form.setFields([{ name: 'status', value: 'open' }])
      form.setFields([{ name: 'start_time_iso_date', value: null }])
      form.setFields([{ name: 'end_time_iso_date', value: null }])
      if (!props.isAdmin && props.currentUserId) {
        setEmployeeId(props.currentUserId)
        form.setFields([{ name: 'employee_id', value: props.currentUserId }])
      }
    }
  }, [props.visible, props.report])

  const customerAssignments = (): Customer['assignments'] | undefined => {
    const customer = props.customers?.find((item) => item._id === customerId)
    if (customer) {
      const employee = customer?.employees.find((item) => JSON.stringify(item._id) === JSON.stringify(employeeId))
      if (employee) {
        return customer.assignments.filter((item) => employee.assignment_ids.includes(item._id))
      }
    }
  }

  useEffect(() => {
    if (!endTimeIsoDate || !startTimeIsoDate) {
      setTotalWorkTIme(undefined)
      form.setFields([{ name: 'data.total_work_time', value: undefined }])
    }
    if (endTimeIsoDate && startTimeIsoDate) {
      let total_work_time = 0

      // Compensate if the midnight limit is crossed.
      if (endTimeIsoDate.getTime() < startTimeIsoDate.getTime()) {
        total_work_time += 24*60*60000
      }

      // After the compensation, it is safe to subtract the start time and lunchbreak.
      total_work_time += endTimeIsoDate.getTime();
      total_work_time -= startTimeIsoDate.getTime()
      total_work_time = total_work_time - 60000 * lunchBreakDuration

      console.log('total worktime', total_work_time)
      setTotalWorkTIme(total_work_time)
      form.setFields([{ name: ['data', 'total_work_time'], value: total_work_time }])
    }
  }, [endTimeIsoDate, startTimeIsoDate, lunchBreakDuration])

  return (
    <Modal
      visible={props.visible}
      onCancel={() => props.onClose()}
      title={<h1>Lisää uusi tuntikirjaus</h1>}
      closeIcon={false}
      closable={false}
      footer={
        <Button.ButtonGroup style={{ justifyContent: 'flex-end' }}>
          <Button
            label={'Sulje'}
            onClick={() => {
              props.onClose()
            }}
            color="default"
            style={{ marginRight: '1em' }}
          />
          <Button
            label={'Tallenna'}
            onClick={() => {
              form.submit()
            }}
            color="green"
          />
        </Button.ButtonGroup>
      }
    >
      <Form
        form={form}
        name="basic"
        initialValues={{ remember: true }}
        onFinish={async (value) => {
          console.log(JSON.stringify(value, null, 2))
          try {
            if (value.assignment_id === 'empty') {
              value.assignment_id = undefined
            }
            if (props.report?._id) {
              await props.onUpdate({ ...value, _id: props.report._id } as unknown as EmployeeReport)
            } else {
              await props.onCreate(value)
            }
          } catch (e) {}
        }}
        onFinishFailed={() => {}}
        autoComplete="off"
        onChange={(value: any) => {
          console.log(value)
        }}
      >
        {/* Työntekijä */}
        <Form.Item label="Työntekijä" name="employee_id" rules={[{ required: true, message: 'Anna työntekijä' }]}>
          <Select
            disabled={!props.isAdmin}
            onChange={(value) => {
              if (value) {
                setEmployeeId(value as any)
                setCustomerId(undefined)
                form.setFields([{ name: 'customer_id', value: undefined }])
              }
            }}
          >
            {props.employees?.map((item) => {
              return (
                <Select.Option value={item._id}>
                  {item.last_name} {item.first_name}
                </Select.Option>
              )
            })}
          </Select>
        </Form.Item>
        {/* Asiakas */}
        <Form.Item label="Asiakas" name="customer_id" rules={[{ required: true, message: 'Anna asiakas' }]}>
          <Select
            disabled={!employeeId}
            onChange={(value) => {
              if (value) {
                setCustomerId(value as any)
                form.setFields([{ name: 'assignment_id', value: undefined }])
              }
            }}
          >
            {props.customers
              ?.filter((c) => {
                return c.employees.find((e) => JSON.stringify(e._id) === JSON.stringify(employeeId))
              })
              .map((item) => {
                return <Select.Option value={item._id}>{item.name}</Select.Option>
              })}
          </Select>
        </Form.Item>
        {/* Toimeksianto */}
        <Form.Item label="Toimeksianto" name="assignment_id">
          <Select disabled={!customerId ? true : false} clearIcon={true}>
            <Select.Option value={'empty'}> </Select.Option>
            {customerAssignments()?.map((item) => {
              return <Select.Option value={item._id}>{item.name}</Select.Option>
            })}
          </Select>
        </Form.Item>

        {/* Päivämäärä */}
        <Form.Item label="Päivämäärä" rules={[{ required: true }]} style={{ marginTop: '2em', display: 'flex' }}>
          <DatePicker
            inputReadOnly={true}
            value={selectedDate ? moment(selectedDate) : undefined}
            style={{ width: '100%' }}
            format="DD.MMM YYYY"
            onChange={(value) => {
              setSelectedDate(value?.toDate())
              form.setFields([{ name: 'start_time_iso_date', value: null }])
              form.setFields([{ name: 'end_time_iso_date', value: null }])
            }}
            showTime={{}}
            disabledDate={(current) => {
              let customDate = moment().add(1, 'days').format('YYYY-MM-DD')
              return current && current > moment(customDate, 'YYYY-MM-DD')
            }}
          />
        </Form.Item>
        {/* Aika */}
        <LocalizationProvider dateAdapter={AdapterDateFns} locale={fiLocale}>
          <Form.Item label={'Työaika'} rules={[{ required: true }]}>
            {/* Aloitus */}
            <Form.Item
              style={{ width: '100%', marginBottom: '0px !important' }}
              name="start_time_iso_date"
              rules={[{ required: true, message: 'Anna alkuaika', type: 'date' }]}
              className={'c-editable-row__disable-margin-bottom'}
            >
              {/* Alkuaika */}
              <MobileTimePicker
                toolbarPlaceholder={''}
                value={null}
                ampm={false}
                disabled={!selectedDate}
                onChange={(value) => {
                  if (value && refSelectedDate.current) {
                    const m = moment(refSelectedDate.current).set({
                      hour: +moment(value).format('HH'),
                      minutes: moment(value).minutes(),
                      milliseconds: 0
                    })
                    const date = new Date(m.toISOString())
                    setStartTimeIsoDate(date)
                    form.setFields([{ name: 'start_time_iso_date', value: m }])
                  }
                }}
                renderInput={(params) => <TextField {...params} />}
              />
            </Form.Item>
            {/* Loppuaika */}
            <Form.Item
              style={{ width: '100%', marginBottom: '0px !important' }}
              name="end_time_iso_date"
              rules={[{ required: true, message: 'Anna loppuaika', type: 'date' }]}
              className={'c-editable-row__disable-margin-bottom'}
            >
              <MobileTimePicker
                value={'undefined'}
                ampm={false}
                toolbarPlaceholder={''}
                disabled={!selectedDate}
                onChange={(value) => {
                  if (value && refSelectedDate.current) {
                    const m = moment(refSelectedDate.current).set({
                      hours: +moment(value).format('HH'),
                      minutes: moment(value).minutes(),
                      milliseconds: 0
                    })
                    const date = m.toDate()
                    setEndTimeIsoDate(date)
                    form.setFields([{ name: 'end_time_iso_date', value: m }])
                  }
                }}
                renderInput={(params) => <TextField {...params} />}
              />
            </Form.Item>
          </Form.Item>
        </LocalizationProvider>

        {/* Työtunnit */}
        <Form.Item
          style={{ display: 'none' }}
          label={'Työtunnit'}
          name={['data', 'total_work_time']}
          rules={[{ required: true, message: 'Anna kaikki arvot laskentaa varten' }]}
          initialValue={millisecondsToDuration(totalWorkTime)}
        >
          <Input disabled={true} value={millisecondsToDuration(totalWorkTime)} />
        </Form.Item>

        <Form.Item label={'Työtunnit'} initialValue={millisecondsToDuration(totalWorkTime)}>
          <Input disabled={true} value={millisecondsToDuration(totalWorkTime)} />
        </Form.Item>

        {/* Ruokatauko */}
        <Form.Item
          label="Ruokatauko"
          style={{ width: '100%', marginTop: '3em' }}
          name={['data', 'lunch_break_duration']}
          rules={[
            {
              pattern: positiveFloatRegex,
              message: 'Anna ruokatauon pituus',
              required: true
            }
          ]}
          normalize={(value: number) => {
            if (value === 0) {
              return 0
            } else {
              return +value
            }
          }}
        >
          <Select
            onChange={(value) => {
              value = value + ''
              if (value) {
                setLunchBreakDuration(+value)
              }
            }}
          >
            <Select.Option value={0}>0 min</Select.Option>
            <Select.Option value={15}>15 min</Select.Option>
            <Select.Option value={30}>30 min</Select.Option>
            <Select.Option value={45}>45 min</Select.Option>
            <Select.Option value={60}>60 min</Select.Option>
          </Select>
        </Form.Item>
        {/* Kilometrikorvaus */}
        <Form.Item
          label={'Kilometrikorvaus'}
          name={['data', 'driving_kilometers']}
          rules={[
            {
              pattern: positiveFloatRegex,
              message: 'Anna luku'
            },
            { type: 'integer', message: 'Anna kokonaisluku' }
          ]}
          normalize={(value: string) => {
            return normalizePositiveFloat(value)
          }}
        >
          <Input type="number" />
        </Form.Item>
        {/* Lisätiedot */}
        <Form.Item label={'Lisätiedot'} name={['data', 'additional_details']} rules={[{ type: 'string' }]}>
          <Input />
        </Form.Item>
        {/* Päiväraha */}
        <Form.Item label={'Päiväraha'} valuePropName="checked" name={['data', 'daily_allowance']} initialValue={false}>
          <Checkbox defaultChecked={false} />
        </Form.Item>
        {/* Ateriatuki */}
        <Form.Item label={'Ateriatuki'} valuePropName="checked" name={['data', 'lunch_support']} initialValue={false}>
          <Checkbox defaultChecked={false} />
        </Form.Item>

        {/* Status */}
        <Form.Item label="Status" name="status" rules={[{ required: true }]} style={{ marginTop: '2em' }}>
          <Select disabled={!props.report?._id}>
            {reportStatuses.map((item) => {
              return (
                <Select.Option value={item.key} disabled={!props.isAdmin && item.key !== 'waiting_for_approval'}>
                  {item.secondary_value}
                </Select.Option>
              )
            })}
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  )
}
